<template>
	<div class="">
		<div class="bg-ihh-purple py-9 w-full justify-center">
			<div class="flex justify-center">
				<a href="/" class="">
					<CheckoutLogo class="w-full" />
				</a>
			</div>
		</div>

		<Spinner v-if="loading" />

		<div v-else class="max-w-6xl mx-auto">
			<div class="w-full lg:px-8 py-4 md2:pt-0 flex justify-center">
				<div class="flex flex-col items-center mb-12 md2:mx-6">
					<div class="lg:border-b mb-2 lg:mb-6 w-full md2:my-12">
						<div class="text-4xl font-ihh-bold text-ihh-dark-gray text-center">Thank you {{ order?.author?.firstName }}!</div>

						<div class="my-6 mb-3 text-ihh-dark-gray px-8 w-full flex justify-center">
							<div class="max-w-3xl">
								<div>
									Your order number is <strong>{{ order?.bcId }}</strong>
								</div>
								<div class="my-3">
									An email will be sent containing information about your purchase. If you have any questions about your purchase, email us at
									<button @click="openMailApp" class="text-ihh-dark-salmon">Help@inhishands.com</button> or call us at
									<button @click="openDialerApp" class="text-ihh-dark-salmon">800-247-4045</button>
								</div>
							</div>
						</div>
					</div>

					<div class="space-y-2 w-full flex flex-col items-center md2:my-12">
						<button class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray flex items-center space-x-2">
							<a href="/">CONTINUE SHOPPING</a>
							<span class="text-2xs">>></span>
						</button>
						<button @click="showSummary = true" class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray text-center space-x-2 md2:hidden">
							Order Summary
						</button>
						<a v-if="feedbackEnabled" href="#order-feedback" class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray text-center space-x-2 md2:hidden">
							Order Feedback
						</a>
					</div>
				</div>

				<div id="desktopSummary" class="flex-col md:items-center p-2 sm:mx-0 mt-4 hidden md2:block">
					<div class="flex-col flex justify-center items-center h-full w-96 px-4">
						<div
							class="flex-col border border-ihh-gray/10 md3:border-ihh-gray/50 rounded bg-ihh-white shadow-lg w-full max-w-2xl h-full sm:max-h-[500px] md3:max-h-none overflow-scroll md3:h-fit md3:max-w-sm"
						>
							<div class="sticky top-0 relative bg-ihh-white text-ihh-midnight-gray text-xl sm:text-4xl text-center flex w-full justify-center z-10">
								<div class="flex m-auto items-center justify-between w-full md3:border-b border-ihh-slate-gray/20">
									<div class="w-full order-2 md3:order-none">
										<div class="w-full border-b">
											<div class="w-full flex justify-between items-center text-base text-ihh-midnight-gray p-4">
												<div class="font-bold">Order Summary</div>
												<div class="text-xs text-ihh-dark-salmon">
													<button @click="printDetails" class="flex items-center lg:space-x-2 sm:space-x-1">
														<div class="icon">
															<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
																<path
																	class="fill-ihh-dark-salmon"
																	d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"
																></path>
															</svg>
														</div>
														<span>Print</span>
													</button>
												</div>
											</div>
										</div>
										<OrderSummaryCart
											:showFullCart="showFullCart"
											:order="order"
											:displayed-products="displayedProducts"
											@show-full-cart="showFullCart = $event"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div id="mobileSummary" v-if="showSummary" class="fixed inset-0 z-50 bg-white overflow-auto md2:hidden text-ihh-midnight-gray">
					<div class="sticky top-0 bg-white border-b px-4 py-6 flex justify-between items-center">
						<div>
							<h2 class="text-lg font-semibold">Order Summary</h2>
							<div class="w-full flex space-x-1">
								<div class="flex justify-start text-start text-base font-normal text-ihh-midnight-gray">
									{{ totalOrderItems }} {{ totalOrderItems === 1 ? 'Item' : 'Items' }}
								</div>
								<div>|</div>
								<div>{{ parseFloat(order?.totalIncTax)?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</div>
							</div>
						</div>
						<div class="lg:hidden order-4 lg:order-none">
							<button
								@click="showSummary = false"
								class="flex group w-7 h-7 p-2 justify-around z-10 hover:bg-ihh-slate-gray bg-ihh-gray/20 rounded-full font-bold text-xl text-ihh-midnight-gray"
							>
								<div class="m-auto items-center flex w-full justify-center">
									<div class="rotate-45 absolute bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
									<div class="-rotate-45 bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
								</div>
							</button>
						</div>
					</div>
					<div class="flex-col flex justify-center items-center w-full py-4">
						<div class="flex-col w-full max-w-2xl mx-auto">
							<OrderSummaryCart
								:order="order"
								:displayed-products="displayedProducts"
								:showFullCart="showFullCart"
								total-class="text-2xl font-bold"
								@show-full-cart="showFullCart = $event"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Order feedback. Filed as an Asana task by POST /controllers/orderFeedback; the server
			     looks up the order itself, so only the order id and checkout id are sent. -->
			<section v-if="feedbackEnabled" id="order-feedback" class="w-full px-4 pb-12">
				<div class="mx-auto w-full max-w-[800px] rounded-lg border border-ihh-gray/20 bg-ihh-white p-6 shadow-sm">
					<h2 class="text-2xl font-ihh-bold text-ihh-dark-gray">Product Feedback</h2>
					<div v-if="feedbackState === 'sent'" class="mt-4 rounded-md bg-ihh-salmon/30 px-4 py-6 text-center text-ihh-dark-gray" role="status">
						<div class="text-lg font-semibold">Thank you for your feedback!</div>
						<p class="mt-1 text-sm">We read every response.</p>
					</div>
					<form v-else class="mt-4 space-y-5" @submit.prevent="submitFeedback">
						<label class="block">
							<span class="block text-sm font-semibold text-ihh-dark-gray">Was there anything you could not find?</span>
							<textarea
								v-model="feedback.notFound"
								rows="3"
								:maxlength="feedbackMaxChars"
								:disabled="feedbackState === 'sending'"
								class="mt-2 w-full rounded-md border border-ihh-form-label px-3 py-2 text-sm text-ihh-dark-gray focus:border-ihh-dark-salmon focus:outline-none focus:ring-2 focus:ring-ihh-salmon"
							></textarea>
						</label>
						<label class="block">
							<span class="block text-sm font-semibold text-ihh-dark-gray">Any other feedback you would like to share?</span>
							<textarea
								v-model="feedback.other"
								rows="3"
								:maxlength="feedbackMaxChars"
								:disabled="feedbackState === 'sending'"
								class="mt-2 w-full rounded-md border border-ihh-form-label px-3 py-2 text-sm text-ihh-dark-gray focus:border-ihh-dark-salmon focus:outline-none focus:ring-2 focus:ring-ihh-salmon"
							></textarea>
						</label>
						<p v-if="feedbackState === 'error'" class="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
							{{ feedbackError }}
						</p>
						<button
							type="submit"
							:disabled="!canSubmitFeedback"
							class="w-40 rounded bg-ihh-dark-salmon px-4 py-2 font-ihh-bold text-white hover:bg-ihh-salmon disabled:cursor-not-allowed disabled:opacity-50"
						>
							{{ feedbackState === 'sending' ? 'Sending…' : feedbackState === 'error' ? 'Try again' : 'Submit' }}
						</button>
					</form>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import CheckoutLogo from '../partials/checkout-logo.vue'
import OrderSummaryCart from '../pages/ihh-items/order-summary-cart.vue'
import Mango from '../../helpers/mango.js'
import axios from 'axios'
import { useCheckoutStore } from '../../stores/store.js'

export default {
	components: {
		CheckoutLogo,
		OrderSummaryCart,
	},

	inject: ['store'],

	setup() {
		const checkout = useCheckoutStore()
		return { checkout }
	},

	data() {
		return {
			loading: true,
			order: null,
			showSummary: false,
			showFullCart: false,
			feedbackPreview: false,
			feedbackEnabled: false, // stays hidden until /controllers/orderFeedback/status says Asana is set up
			feedback: { notFound: '', other: '' },
			feedbackState: 'idle', // idle | sending | sent | error
			feedbackError: '',
			feedbackMaxChars: 2000,
		}
	},

	methods: {
		async loadFeedbackStatus() {
			try {
				const { data } = await axios.get(`${this.store.api}/controllers/orderFeedback/status`)
				this.feedbackEnabled = data?.enabled === true
			} catch {
				this.feedbackEnabled = false
			}
		},
		async submitFeedback() {
			if (!this.canSubmitFeedback) return
			this.feedbackState = 'sending'
			this.feedbackError = ''
			try {
				const { data } = await axios.post(`${this.store.api}/controllers/orderFeedback`, {
					orderId: this.order?.bcId,
					checkoutId: this.order?.cartId || this.order?.bcCartId || '',
					notFound: this.feedback.notFound,
					other: this.feedback.other,
				})
				if (!data?.ok) throw Object.assign(new Error('feedback rejected'), { friendly: data?.message })
				this.feedbackState = 'sent'
			} catch (error) {
				this.feedbackError = error?.friendly || error?.response?.data?.message || 'We could not send your feedback right now. Please try again.'
				this.feedbackState = 'error'
			}
		},
		openMailApp() {
			let subject
			console.log('Mail app user: ', this.store.user?.id)

			if (this.store?.user?.id) {
				console.log(this.store.user.firstName, this.store.user.lastName)
				subject = `${this.store?.user?.firstName} ${this.store?.user?.lastName} - ${this.store?.user?.email}`
			} else {
				subject = ''
			}

			const mailtoLink = `mailto:help@inhishands.com?subject=${subject}`
			window.location.href = mailtoLink
		},
		openDialerApp() {
			const phoneNumber = '1-800-247-4045'
			const telLink = `tel:${phoneNumber}`
			window.location.href = telLink
		},
		printDetails() {
			this.showSummary = false
			this.showFullCart = true
			setTimeout(() => {
				window.print()
			}, 100)
		},
	},

	async mounted() {
		const bcId = Number(this.$route.params.bcId)
		this.loading = true

		// Admin-only preview (?feedbackPreview=1) for testing the feedback form on an existing
		// order: skips the just-checked-out check and sends no conversion events. Customers
		// without the admin role get the normal behaviour.
		this.feedbackPreview = this.$route.query?.feedbackPreview === '1' && !!this.store?.user?.roles?.includes('admin')

		if (!this.checkout?.checkoutSuccess && !this.feedbackPreview) {
			// Only load order confirmation if redirected from successful checkout
			this.$router.push('/')
			return
		}

		this.loadFeedbackStatus()

		if (!this.store?.user?.id && !this.feedbackPreview) {
			this.order = JSON.parse(window.localStorage.getItem('guestOrder'))
			console.log('Guest Order id: ', this.order?.bcId)
		} else {
			console.log('Reading order through sync endpoint...')
			// Best effort: the webhook may already have synced it, and the read below is what matters.
			const syncResponse = await axios.get(`${this.store.api}/controllers/bigCommerce/sync/orders/${bcId}`).catch(() => null)
			console.log('Sync response: ', syncResponse?.data)
			const ordersResponse = await Mango.orders({ search: { bcId: bcId } })
			console.log('Order id: ', ordersResponse[0]?.id)
			this.order = ordersResponse[0] // test
		}

		this.loading = false

		// A preview is not a purchase.
		if (this.feedbackPreview) return

		if (typeof window.gtag === 'function') {
			console.log('Sending gtag conversion event...')
			// Google ads conversion
			try {
				window.gtag('event', 'conversion', {
					send_to: 'AW-387509916/yWzPCPSGh7EaEJzd47gB',
					value: this.order?.totalIncTax,
					currency: 'USD',
				})
			} catch (error) {
				console.error('Error sending gtag event: ', error)
				console.log('error stack trace: ', error.stack)
			}

			// Google Analytics purchase event
			console.log('Sending gtag purchase event...')
			try {
				console.log('purchase event data: ', {
					send_to: 'G-SLGVZKFN0K', // Your GA property ID
					transaction_id: this.order?.bcId?.toString(),
					value: this.order?.totalIncTax,
					tax: this.order?.totalTax,
					shipping: this.order?.shippingCostIncTax,
					currency: 'USD',
					items:
						this.order?.products?.map((item) => ({
							item_id: `SKU_${item?.sku}` || '', // Product ID
							item_name: item.name || '', // Product name
							price: item.priceExTax || 0, // Price per item
							quantity: item.quantity || 1, // Quantity
							item_brand: item?.brand || '', // Product brand
							item_variant: item?.variantId?.toString() || '', // Product variant
						})) || [], // Array of items in the order
				})
				window.gtag('event', 'purchase', {
					send_to: 'G-SLGVZKFN0K', // Your GA property ID
					transaction_id: this.order?.bcId?.toString(),
					value: this.order?.totalIncTax,
					tax: this.order?.totalTax,
					shipping: this.order?.shippingCostIncTax,
					currency: 'USD',
					items:
						this.order?.products?.map((item) => ({
							item_id: `SKU_${item?.sku}` || '', // Product ID
							item_name: item.name || '', // Product name
							price: item.priceExTax || 0, // Price per item
							quantity: item.quantity || 1, // Quantity
							item_brand: item?.brand || '', // Product brand
							item_variant: item?.variantId?.toString() || '', // Product variant
						})) || [], // Array of items in the order
				})
			} catch (error) {
				console.error('Error sending gtag event: ', error)
				console.log('error stack trace: ', error.stack)
			}
		} else {
			console.log('gtag window object not found')
		}
	},

	beforeUnmount() {
		if (this.feedbackPreview) return
		this.checkout.checkoutSuccess = false
		window.localStorage.removeItem('guestOrder')
	},

	computed: {
		canSubmitFeedback() {
			if (this.feedbackState === 'sending' || !this.order?.bcId) return false
			return !!(this.feedback.notFound.trim() || this.feedback.other.trim())
		},
		totalOrderItems() {
			return (
				this.order?.products?.reduce((total, product) => {
					return total + (product.quantity || product.qty || 0)
				}, 0) || 0
			)
		},
		displayedProducts() {
			return this.order?.products?.slice(0, this.showFullCart ? undefined : 4) || []
		},
	},
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');

@media print {
	@page {
		size: landscape;
	}

	.bg-ihh-purple,
	button:not(.print-show),
	#order-feedback,
	.fixed {
		display: none !important;
	}

	#desktopSummary {
		display: block !important;
		width: 100% !important;
		max-width: none !important;
	}

	.w-96 {
		width: 100% !important;
	}

	.overflow-scroll {
		overflow: visible !important;
	}

	.shadow-lg {
		box-shadow: none !important;
	}

	* {
		color: black !important;
		text-align: left !important;
	}
}
</style>
